import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import { useState } from "react";

interface LinkedInPreviewProps {
  content: string;
  images: string[];
  video?: string | null;
  onImageRemove?: (imageUrl: string) => void;
  onVideoRemove?: () => void;
  profilePicture?: string | null;
  linkedinName?: string | null;
  linkedinHeadline?: string | null;
}

export const LinkedInPreview = ({ content, images, video, onImageRemove, onVideoRemove, profilePicture, linkedinName, linkedinHeadline }: LinkedInPreviewProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const truncateText = (text: string) => {
    // Create a temporary div to parse HTML
    const div = document.createElement('div');
    div.innerHTML = text;
    
    // Instead of using textContent, we'll work with innerHTML
    const cleanText = div.innerHTML;
    
    // If the cleaned text is empty, return early
    if (!cleanText) {
      return {
        truncated: '',
        hasMore: false
      };
    }
    
    // Split by <br> or newline tags for line counting
    const lines = cleanText.split(/<br\s*\/?>/i);
    if (lines.length > 3 || cleanText.length > 235) {
      // If there's a newline in first 3 lines, truncate at first newline
      if (lines.length > 1 && lines[0].length + lines[1].length < 235) {
        return {
          truncated: lines[0],
          hasMore: true
        };
      }
      // For HTML content, we need a more careful truncation
      const truncated = cleanText.substring(0, 235);
      // Ensure we don't cut in the middle of an HTML tag
      const lastOpenBracket = truncated.lastIndexOf('<');
      const lastCloseBracket = truncated.lastIndexOf('>');
      
      let finalTruncated = truncated;
      if (lastOpenBracket > lastCloseBracket) {
        finalTruncated = truncated.substring(0, lastOpenBracket);
      }
      
      return {
        truncated: finalTruncated,
        hasMore: true
      };
    }
    return {
      truncated: cleanText,
      hasMore: false
    };
  };

  const cleanHtmlContent = (text: string) => {
    // Create temporary div to parse HTML
    const div = document.createElement('div');
    div.innerHTML = text;
    
    // Handle both formats:
    // 1. Editor's HTML format (spans with data-urn)
    // 2. Already formatted mentions (spans with text-blue-600)
    const mentionSpans = div.querySelectorAll('span[data-urn], span.text-blue-600');
    mentionSpans.forEach(span => {
      if (span.hasAttribute('data-urn')) {
        // Convert from editor format to preview format
        const name = span.textContent;
        span.outerHTML = `<span class="text-blue-600 font-bold">${name}</span>`;
      } else {
        // Already in preview format, ensure it has both classes
        span.className = 'text-blue-600 font-bold';
      }
    });
    
    return div.innerHTML;
  };

  const { truncated, hasMore } = truncateText(content);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header section */}
      <div className="p-3 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden">
            {profilePicture && (
              <img 
                src={profilePicture} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="flex flex-col">
            <div className="font-semibold text-[14px] text-[#000000] hover:text-blue-600 cursor-pointer">
              {linkedinName || "Your Name"}
            </div>
            <div className="text-[12px] text-gray-500">
              {linkedinHeadline || "Your Headline"}
            </div>
            <div className="flex items-center gap-1 text-[12px] text-gray-500">
              <span>7h</span>
              <span>·</span>
              <img 
                src="/lovable-uploads/f896c5ab-c9bd-4507-8df9-74b32a27b22a.png" 
                alt="Global"
                className="w-3 h-3 cursor-pointer hover:opacity-80"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="px-3 pb-2">
        <div 
          className="text-[14px] text-[#000000] whitespace-pre-line"
          dangerouslySetInnerHTML={{ 
            __html: isExpanded ? cleanHtmlContent(content) : cleanHtmlContent(truncated)
          }}
        />
        {hasMore && !isExpanded && (
          <span 
            onClick={() => setIsExpanded(true)}
            className="text-gray-500 hover:text-gray-700 ml-1 cursor-pointer"
          >
            ...more
          </span>
        )}
      </div>
      
      {/* Video preview section */}
      {video && (
        <div className="border-t border-gray-100 mt-2">
          <div className="relative group p-3">
            <AspectRatio ratio={16/9}>
              <video
                src={video}
                controls
                className="w-full h-full object-cover rounded"
                controlsList="noplaybackrate nofullscreen nodownload"
                style={{ 
                  position: 'relative',
                  maxWidth: '100%'
                }}
              />
            </AspectRatio>
            {onVideoRemove && (
              <button
                onClick={onVideoRemove}
                className="absolute top-5 right-5 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Existing images section */}
      {images.length > 0 && !video && (
        <div className="border-t border-gray-100 mt-2">
          <ScrollArea className="h-[300px]">
            <div className="grid grid-cols-2 gap-0.5 bg-gray-100">
              {images.map((imageUrl, index) => (
                <div key={index} className="relative group">
                  <AspectRatio ratio={1}>
                    <img
                      src={imageUrl}
                      alt={`Preview ${index + 1}`}
                      className="object-cover w-full h-full"
                    />
                  </AspectRatio>
                  {onImageRemove && (
                    <button
                      onClick={() => onImageRemove(imageUrl)}
                      className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Engagement metrics */}
      <div className="px-3 py-1 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div className="flex -space-x-[3px]">
            <img 
              src="/lovable-uploads/a1c0b3c7-7f25-4e6e-aa7c-c56acdb4b968.png" 
              alt="Like"
              className="w-4 h-4 z-10"
            />
            <img 
              src="/lovable-uploads/a7e0ece0-e3d6-496e-a9ec-8f29dee95308.png" 
              alt="Insightful"
              className="w-4 h-4"
            />
          </div>
          <span className="text-[13px] text-gray-500">48</span>
        </div>
        <div className="text-[13px] text-gray-500">
          9 comments
        </div>
      </div>

      {/* Social actions */}
      <div className="border-t border-gray-200">
        <div className="flex items-center justify-center py-3">
          <div className="grid grid-cols-4 w-full px-4 md:px-6 gap-x-2 md:gap-x-4">
            <div className="flex flex-col items-center gap-1 text-[#666666] hover:text-[#404040] cursor-pointer">
              <img 
                src="/lovable-uploads/b355f2c9-b5d5-4e91-98cb-58f06250624e.png" 
                alt="Like"
                className="w-5 h-5"
              />
              <span className="text-[15px] font-medium">Like</span>
            </div>
            <div className="flex flex-col items-center gap-1 text-[#666666] hover:text-[#404040] cursor-pointer">
              <img 
                src="/lovable-uploads/62581de2-a304-4229-91e0-72dce1904dd9.png" 
                alt="Comment"
                className="w-5 h-5"
              />
              <span className="text-[15px] font-medium">Comment</span>
            </div>
            <div className="flex flex-col items-center gap-1 text-[#666666] hover:text-[#404040] cursor-pointer">
              <img 
                src="/lovable-uploads/5b82ca80-52d7-4aa1-8c54-0b289cf58fcb.png" 
                alt="Repost"
                className="w-5 h-5"
              />
              <span className="text-[15px] font-medium">Repost</span>
            </div>
            <div className="flex flex-col items-center gap-1 text-[#666666] hover:text-[#404040] cursor-pointer">
              <img 
                src="/lovable-uploads/60751420-ffdf-4fbd-b819-d814f6044704.png" 
                alt="Send"
                className="w-5 h-5"
              />
              <span className="text-[15px] font-medium">Send</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
